class GlossaryIndex extends HTMLElement {
  constructor() {
    super();

    this.items = new Map();
    this.activeLetter = '';
    this.query = '';
    this.isLoading = false;
  }

  connectedCallback() {
    this.groups = this.querySelector('[data-glossary-groups]');
    this.searchForm = this.querySelector('[data-glossary-search]');
    this.searchInput = this.querySelector('[data-glossary-query]');
    this.resetButton = this.querySelector('[data-glossary-reset]');
    this.clearButton = this.querySelector('[data-glossary-clear]');
    this.emptyState = this.querySelector('[data-glossary-empty]');
    this.count = this.querySelector('[data-glossary-count]');
    this.loadingStatus = this.querySelector('[data-glossary-loading]');
    this.pagination = this.querySelector('[data-glossary-pagination]');
    this.letterButtons = [...this.querySelectorAll('[data-glossary-letter]')];

    if (!this.groups || !this.searchInput) return;

    this.collectItems(this);
    this.readUrlState();
    this.bindEvents();
    this.render();
    this.loadRemainingPages();
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this.onPopState);
  }

  bindEvents() {
    this.searchForm?.addEventListener('submit', (event) => {
      event.preventDefault();
      this.applySearch(this.searchInput.value, true);
    });

    this.searchInput.addEventListener('input', () => {
      this.applySearch(this.searchInput.value, false);
    });

    this.searchForm?.addEventListener('reset', () => {
      requestAnimationFrame(() => this.applySearch('', true));
    });

    this.letterButtons.forEach((button) => {
      button.addEventListener('click', () => {
        this.activeLetter = button.dataset.glossaryLetter || '';
        this.updateUrl(true);
        this.render();
      });
    });

    this.clearButton?.addEventListener('click', () => {
      this.activeLetter = '';
      this.searchInput.value = '';
      this.query = '';
      this.updateUrl(true);
      this.render();
      this.searchInput.focus();
    });

    this.onPopState = () => {
      this.readUrlState();
      this.render();
    };
    window.addEventListener('popstate', this.onPopState);
  }

  collectItems(root) {
    root.querySelectorAll('[data-glossary-item]').forEach((item) => {
      const url = item.dataset.glossaryUrl;
      const id = item.dataset.glossaryId || url;
      if (!url || this.items.has(id)) return;

      const title = item.dataset.glossaryTitle || item.textContent.trim();
      item.dataset.glossaryTitle = title;
      item.dataset.glossaryLetter = this.getLetter(title);
      this.items.set(id, item);
    });
  }

  async loadRemainingPages() {
    const currentPage = Number(this.dataset.currentPage || 1);
    let nextUrl = currentPage > 1
      ? new URL(this.dataset.firstPageUrl, window.location.origin).href
      : this.querySelector('[data-glossary-next]')?.href;
    if (!nextUrl || this.isLoading) {
      this.updateAvailableLetters();
      return;
    }

    this.isLoading = true;
    this.groups.setAttribute('aria-busy', 'true');
    if (this.pagination) this.pagination.hidden = true;
    this.loadingStatus.textContent = this.dataset.loadingLabel || '';

    try {
      while (nextUrl) {
        const response = await fetch(nextUrl, { credentials: 'same-origin' });
        if (!response.ok) throw new Error(`Glossary page request failed: ${response.status}`);

        const html = await response.text();
        const page = new DOMParser().parseFromString(html, 'text/html');
        const incoming = page.querySelector(`glossary-index[data-section-id="${CSS.escape(this.dataset.sectionId)}"]`);
        if (!incoming) throw new Error('Glossary section missing in paginated response');

        incoming.querySelectorAll('[data-glossary-item]').forEach((item) => {
          const url = item.dataset.glossaryUrl;
          const id = item.dataset.glossaryId || url;
          if (!url || this.items.has(id)) return;

          const importedItem = document.importNode(item, true);
          const title = importedItem.dataset.glossaryTitle || importedItem.textContent.trim();
          importedItem.dataset.glossaryTitle = title;
          importedItem.dataset.glossaryLetter = this.getLetter(title);
          this.items.set(id, importedItem);
        });

        const nextLink = incoming.querySelector('[data-glossary-next]');
        nextUrl = nextLink ? new URL(nextLink.getAttribute('href'), nextUrl).href : null;
      }

      this.loadingStatus.textContent = '';
      this.updateAvailableLetters();
      this.render();
    } catch (error) {
      console.warn(error);
      this.loadingStatus.textContent = this.dataset.errorLabel || '';
      if (this.pagination) this.pagination.hidden = false;
    } finally {
      this.isLoading = false;
      this.groups.setAttribute('aria-busy', 'false');
    }
  }

  applySearch(value, pushState) {
    this.query = value.trim();
    this.updateUrl(pushState);
    this.render();
  }

  readUrlState() {
    const params = new URLSearchParams(window.location.search);
    const letter = (params.get('letter') || '').toLocaleUpperCase('de-DE');

    this.activeLetter = this.letterButtons.some((button) => button.dataset.glossaryLetter === letter) ? letter : '';
    this.query = (params.get('q') || '').trim();
    this.searchInput.value = this.query;
  }

  updateUrl(pushState) {
    const url = new URL(window.location.href);

    if (this.activeLetter) url.searchParams.set('letter', this.activeLetter);
    else url.searchParams.delete('letter');

    if (this.query) url.searchParams.set('q', this.query);
    else url.searchParams.delete('q');

    url.searchParams.delete('page');
    if (this.dataset.pageParam) url.searchParams.delete(this.dataset.pageParam);
    window.history[pushState ? 'pushState' : 'replaceState']({}, '', url);
  }

  updateAvailableLetters() {
    const availableLetters = new Set([...this.items.values()].map((item) => item.dataset.glossaryLetter));

    this.letterButtons.forEach((button) => {
      const letter = button.dataset.glossaryLetter;
      button.disabled = Boolean(letter) && !availableLetters.has(letter);
    });
  }

  render() {
    const normalizedQuery = this.normalize(this.query);
    const visibleItems = [...this.items.values()]
      .filter((item) => {
        const matchesLetter = !this.activeLetter || item.dataset.glossaryLetter === this.activeLetter;
        const matchesQuery = !normalizedQuery || this.normalize(item.dataset.glossaryTitle).includes(normalizedQuery);
        return matchesLetter && matchesQuery;
      })
      .sort((first, second) => first.dataset.glossaryTitle.localeCompare(second.dataset.glossaryTitle, 'de', {
        sensitivity: 'base',
        numeric: true,
      }));

    const itemsByLetter = new Map();
    visibleItems.forEach((item) => {
      const letter = item.dataset.glossaryLetter;
      if (!itemsByLetter.has(letter)) itemsByLetter.set(letter, []);
      itemsByLetter.get(letter).push(item);
    });

    const fragment = document.createDocumentFragment();
    [...itemsByLetter.keys()].sort((a, b) => a.localeCompare(b, 'de')).forEach((letter) => {
      const group = document.createElement('section');
      group.className = 'glossary-index__group';
      group.dataset.glossaryGroup = letter;

      const heading = document.createElement(this.dataset.groupHeadingLevel === '3' ? 'h3' : 'h2');
      heading.className = 'glossary-index__group-letter';
      heading.textContent = letter;

      const list = document.createElement('ul');
      list.className = 'glossary-index__terms list-unstyled';
      list.setAttribute('role', 'list');
      list.append(...itemsByLetter.get(letter));

      group.append(heading, list);
      fragment.append(group);
    });

    this.groups.replaceChildren(fragment);
    this.emptyState.hidden = visibleItems.length > 0;
    this.groups.hidden = visibleItems.length === 0;
    const singular = this.dataset.singularLabel || 'Begriff';
    const plural = this.dataset.pluralLabel || 'Begriffe';
    this.count.textContent = `${visibleItems.length} ${visibleItems.length === 1 ? singular : plural}`;
    this.resetButton.hidden = !this.query;

    this.letterButtons.forEach((button) => {
      const isActive = (button.dataset.glossaryLetter || '') === this.activeLetter;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-pressed', String(isActive));
    });
  }

  getLetter(title) {
    const firstCharacter = this.normalize(title).charAt(0);
    return /^[A-Z]$/.test(firstCharacter) ? firstCharacter : '#';
  }

  normalize(value) {
    return String(value)
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/ß/g, 'ss')
      .toLocaleUpperCase('de-DE');
  }
}

if (!customElements.get('glossary-index')) {
  customElements.define('glossary-index', GlossaryIndex);
}
